import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useParams } from "react-router-dom";

import {
  NotionEditor,
  LoadingSpinner,
} from "containers/Tiptap/components/tiptap-templates/notion-like/notion-like-editor";
import { TabComponentProps } from "components/Entities/EntityDetailsContent/types";
import { useEditorStore } from "containers/Tiptap/hooks/useEditorStore";
import { DocumentViewMode } from "containers/Tiptap/components/tiptap-extension/document-settings-yjs";

import "./notion-style-editor.scss";

interface NotionEditorReloadableProps {
  entityId: string;
  viewMode: DocumentViewMode;
  initialLastSavedAt?: string | null;
  initialLastSavedBy?: { uuid: string; name: string } | null;
}

const NotionEditorReloadable: React.FC<NotionEditorReloadableProps> = ({
  entityId,
  viewMode,
  initialLastSavedAt,
  initialLastSavedBy,
}) => {
  const [renderedViewMode, setRenderedViewMode] = useState<DocumentViewMode>(viewMode);
  const isReloading = renderedViewMode !== viewMode;

  useEffect(() => {
    if (renderedViewMode === viewMode) return undefined;
    // Double rAF so the reloading state paints before the blocking remount runs.
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setRenderedViewMode(viewMode));
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
  }, [viewMode, renderedViewMode]);

  if (isReloading) {
    return <LoadingSpinner text="Reloading document..." />;
  }

  return (
    <NotionEditor
      key={renderedViewMode}
      room={entityId}
      placeholder="Type '/' for commands..."
      initialLastSavedAt={initialLastSavedAt}
      initialLastSavedBy={initialLastSavedBy}
    />
  );
};

export const NotionStyleEditor: React.FC<TabComponentProps> = observer(({ store }) => {
  const params = useParams<{ entityType: string; entityId: string }>();
  const editorStore = useEditorStore();

  if (!params.entityId) {
    return (
      <div className="notion-style-error">
        <p>No document ID provided.</p>
      </div>
    );
  }

  return (
    // Keyed per-document so a stale editor or pending import can't leak across navigation.
    <NotionEditorReloadable
      key={params.entityId}
      entityId={params.entityId}
      viewMode={editorStore.viewMode}
      initialLastSavedAt={store.details?.lastSavedAt}
      initialLastSavedBy={store.details?.lastSavedBy}
    />
  );
});
